import tinycolor from 'tinycolor2';

/**
 * Die Kundenfarbe in der Schreibweise, die shadcn erwartet.
 *
 * Im Portal stehen zwei Farbsysteme nebeneinander. Das ältere kommt aus dem Kundenthema und schreibt
 * `--primary-color`, `--customer-primary…` und `--theme-primary-light` als Hex-Werte. Das jüngere
 * kam mit shadcn und liest `--primary`, `--ring` und Verwandte — als **HSL-Tripel ohne Funktion**
 * (`222.2 47.4% 11.2%`), weil Tailwind sie in `hsl(var(--primary) / <alpha>)` einsetzt und dafür die
 * nackten Zahlen braucht.
 *
 * Solange nur das erste System gefüllt wurde, zeigten die shadcn-Komponenten ihren eingebauten Wert
 * — ein dunkles Schiefergrau-Blau (#0F172A) — unabhängig davon, welcher Kunde angemeldet ist. Das
 * ist kein fehlendes Blau, sondern das falsche, und es betrifft mehr Dateien als das Kundensystem
 * erreicht.
 */

/** `#001A61` → `222 100% 19%`. Leer für alles, was keine Farbe ist. */
export const hslTriplet = (color: unknown): string => {
    if (typeof color !== 'string' || !color.trim()) return '';

    const parsed = tinycolor(color);
    if (!parsed.isValid()) return '';

    const { h, s, l } = parsed.toHsl();

    // Gerundet, weil die Zahlen im Stylesheet landen und dort niemand sechzehn Nachkommastellen
    // lesen will. Ein Zehntelgrad Farbton ist nicht zu sehen.
    const round = (value: number) => Math.round(value * 10) / 10;

    return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
};

const DARK_FOREGROUND = '222.2 47.4% 11.2%';
const LIGHT_FOREGROUND = '0 0% 100%';

/**
 * Relative Luminanz (0 = schwarz, 1 = weiß), ab der ein Grund als hell gilt und dunkle Schrift
 * bekommt. Die Mitte der Skala: Pastelltöne wie der Lavendel #C3A9D6 (0,45) zählen noch als farbig
 * und tragen weiße Schrift, erst wirklich helle Gründe wie Gelb oder Weiß bekommen dunkle.
 */
const LIGHT_BACKGROUND_LUMINANCE = 0.5;

/**
 * Die Schrift auf dieser Farbe — als HSL-Tripel wie oben: weiß auf dunklem, dunkel auf hellem Grund.
 *
 * Gemessen an der relativen Luminanz nach WCAG, nicht an `tinycolor.isLight` (einfacher
 * Helligkeitsschnitt der Kanäle) und nicht am Kontrastmaximum — das kippt schon bei mittleren Tönen
 * auf dunkle Schrift, obwohl die Fläche als Farbe gelesen wird. Trägt Tooltips, Knöpfe und alles
 * andere auf `bg-primary`.
 */
export const foregroundTriplet = (color: unknown): string => {
    if (typeof color !== 'string' || !color.trim()) return '';

    const parsed = tinycolor(color);
    if (!parsed.isValid()) return '';

    return parsed.getLuminance() > LIGHT_BACKGROUND_LUMINANCE ? DARK_FOREGROUND : LIGHT_FOREGROUND;
};

/**
 * Welche shadcn-Variablen aus der Kundenfarbe kommen.
 *
 * Bewusst drei und nicht alle. `--primary` und `--primary-foreground` sind die Markenfarbe und ihre
 * Schrift; `--ring` ist der Fokusrahmen, der zur Marke gehört, weil er neben jedem Knopf steht.
 *
 * `--accent` bleibt außen vor, obwohl der Name das Gegenteil nahelegt: In shadcn ist das kein Akzent,
 * sondern der helle Grund, den eine Zeile beim Überfahren annimmt. In der Markenfarbe eingefärbt
 * bekäme jede Hover-Fläche der Anwendung einen Farbstich.
 */
export const shadcnTokensFor = (primary: unknown): Record<string, string> => {
    const value = hslTriplet(primary);
    if (!value) return {};

    return {
        '--primary': value,
        '--primary-foreground': foregroundTriplet(primary),
        '--ring': value,
    };
};

/**
 * Die Abstufungen der Primärfarbe, aus ihr gerechnet.
 *
 * `styleCalculator` trägt je Feld eine Richtung und einen Faktor — die Absicht ist erkennbar, dass
 * ein Kunde **eine** Farbe wählt und der Rest daraus folgt. Gespeichert wird aber jedes Feld
 * einzeln, und wer im Firmenprofil nur die Primärfarbe ändert, lässt die übrigen stehen.
 *
 * Genau das ist überall passiert: Bei jedem geprüften Mandanten stehen `primaryDark` und
 * `primaryDarker` auf `#000000` und die hellen Stufen auf nahezu Weiß — die Werte, die aus der
 * schwarzen Voreinstellung entstehen. Zwanzig Dateien färben mit `--customer-primary-dark`, und
 * schwarz ist dort keine Abstufung der Hausfarbe, sondern deren Abwesenheit.
 *
 * Deshalb wird die Familie hier gerechnet statt gelesen. Der Kunde behält die Farbe, die er
 * gewählt hat; was daraus folgt, folgt daraus.
 */
type FamilyStep = { field: string; direction: number; factor: number };

const PRIMARY_FAMILY: FamilyStep[] = [
    { field: 'primaryDarker', direction: -1, factor: 10 },
    { field: 'primaryDark', direction: -1, factor: 5 },
    { field: 'primary', direction: 1, factor: 0 },
    { field: 'primaryLight', direction: 1, factor: 30 },
    { field: 'primaryLighter', direction: 1, factor: 70 },
];

/** Die Faktoren, mit denen `styleCalculator` in Theme.tsx die Sekundärfamilie beschreibt. */
const SECONDARY_FAMILY: FamilyStep[] = [
    { field: 'secondaryDarker', direction: -1, factor: 10 },
    { field: 'secondaryDark', direction: -1, factor: 5 },
    { field: 'secondary', direction: 1, factor: 0 },
    { field: 'secondaryLight', direction: 1, factor: 40 },
    { field: 'secondaryLighter', direction: 1, factor: 45 },
];

const colorFamily = (steps: FamilyStep[], color: unknown): Record<string, string> => {
    if (typeof color !== 'string' || !color.trim()) return {};

    const base = tinycolor(color);
    if (!base.isValid()) return {};

    return steps.reduce<Record<string, string>>((map, { field, direction, factor }) => {
        map[field] =
            direction > 0
                ? tinycolor(color).lighten(factor).toHexString()
                : tinycolor(color).darken(factor).toHexString();

        return map;
    }, {});
};

export const primaryFamily = (primary: unknown): Record<string, string> => colorFamily(PRIMARY_FAMILY, primary);

/**
 * Die Abstufungen der Sekundärfarbe, aus ihr gerechnet — aus demselben Grund wie bei der Primären.
 *
 * `CustomerTheme` speichert nur `secondary`; die Stufen kamen bisher aus der Voreinstellung
 * (`#5F3A79`) und passten zu keiner gewählten Sekundärfarbe. Das Firmenprofil bietet seit dem
 * Umbau auf zwei Farben nur noch die Grundfarbe an; was daraus folgt, folgt daraus.
 */
export const secondaryFamily = (secondary: unknown): Record<string, string> =>
    colorFamily(SECONDARY_FAMILY, secondary);

/**
 * Das Thema, wie die Oberfläche es benutzen soll.
 *
 * Einmal beim Laden angewandt, damit alle drei Wege dieselbe Farbe sehen: die Fluent-Komponenten
 * über `calculatePartialTheme`, die älteren CSS-Variablen und die shadcn-Tokens. Würde nur einer
 * davon gerichtet, widersprächen sich zwei Hälften derselben Seite.
 */
export const normalizeTheme = (theme: any): any =>
    theme && typeof theme === 'object'
        ? { ...theme, ...primaryFamily(theme.primary), ...secondaryFamily(theme.secondary) }
        : theme;
